"use client";

import { useMemo, type ComponentProps, type ReactNode } from "react";
import { useNow } from "@/lib/hooks/useNow";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useAuth } from "@clerk/nextjs";
import Image from "next/image";
import dynamic from "next/dynamic";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { CheckCircleFilled } from "@/components/icons/CheckCircleFilled";
import { WarningTriangle } from "@/components/icons/WarningTriangle";

import { Button } from "@/components/ui/button";
import { GridContainer } from "@/components/layout/GridContainer";
import { EventTag, type EventTagVariant } from "@/components/layout/EventTag";
import { ListBlock } from "@/components/layout/ListBlock";
import { ListItem } from "@/components/layout/ListItem";
import { type ListItemIconName } from "@/components/icons";
import { MemberLockIcon } from "@/components/icons/MemberLockIcon";
import {
  MaskShape,
  type MaskShapeVariant,
} from "@/components/illustrations/MaskShape";
import { VideoSkeleton } from "@/components/layout/Skeleton";
import { cn } from "@/lib/utils";
import { setReturnIntent } from "@/lib/auth/return-intent";
import type { EventIconName } from "@/components/icons";

// Map event types to their corresponding colors (matching EventTag bgClass)
const eventTypeColorClasses: Record<EventIconName, string> = {
  diaTalk: "text-sand-100",
  topicTalk: "text-sky-75",
  sprechstunde: "text-sky-100",
  community: "text-sun-100",
  reisen: "text-sun-50",
  reiseInfo: "text-sun-50",
};

const getEventTypeColorClass = (eventType: string): string => {
  if (eventType in eventTypeColorClasses) {
    return eventTypeColorClasses[eventType as EventIconName];
  }
  return "text-sun-100"; // Fallback to sun-100 when no event type
};

const MuxPlayer = dynamic(() => import("@mux/mux-player-react"), {
  ssr: false,
  loading: () => <VideoSkeleton className="w-full h-full" />,
});

type InfoItem = {
  icon: ListItemIconName;
  text: string;
};

type EventHeroProps = {
  // Event type for tag
  eventType: EventTagVariant;

  // Core event data
  date: Date;
  endDate?: Date;
  subline?: string;
  title: string;
  description?: ReactNode;

  // Media
  image?: { src: string; alt: string };
  playbackId?: string;
  maskShape?: MaskShapeVariant;

  // Info items array (flexible list of icon + text)
  infoItems?: InfoItem[];

  // CTA
  ctaLabel?: string;
  ctaLink?: string;
  hideCta?: boolean;
  onCtaClick?: () => void;

  // Member access
  memberOnly?: boolean;
  isMember?: boolean;

  // Signup status
  isSignedUp?: boolean;
  dateChanged?: boolean;
  isCancelled?: boolean;
  onCancelSignup?: () => void;
} & ComponentProps<"section">;

const MaskedMedia = ({
  image,
  playbackId,
  shape = "four_vertical_even",
  showVideo,
  eventType,
}: {
  image?: { src: string; alt: string };
  playbackId?: string;
  shape?: MaskShapeVariant;
  showVideo: boolean;
  eventType?: string;
}) => {
  const hasImage = image?.src;

  // If no image and no video, show the mask shape as a colored fallback
  if (!hasImage && !showVideo) {
    const colorClass = getEventTypeColorClass(eventType || "");
    return (
      <div className="relative w-full aspect-[345/167] overflow-hidden">
        <MaskShape
          variant={shape}
          className={cn("w-full h-full", colorClass)}
          preserveAspectRatio="none"
        />
      </div>
    );
  }

  // Video after event: no mask, just rounded corners
  if (showVideo && playbackId) {
    return (
      <div className="relative w-full aspect-video rounded-3xl overflow-hidden">
        <MuxPlayer
          streamType="on-demand"
          playbackId={playbackId}
          poster={image?.src}
          preload="none"
          forwardSeekOffset={0}
          backwardSeekOffset={0}
          className="w-full h-full object-cover"
          style={{ "--media-border-radius": "32px", "--media-background-color": "transparent" } as React.CSSProperties}
          accentColor="#FDFD92"
        />
      </div>
    );
  }

  // Image before event: use mask shape via inline SVG for maximum compatibility.
  // viewBox matches the container aspect ratio so the image is not distorted.
  // clipPathUnits="objectBoundingBox" means the 0-1 normalized paths work regardless of viewBox.
  return (
    <>
      {/* Mobile */}
      <div className="md:hidden relative w-full aspect-[345/167] overflow-hidden rounded-2xl">
        <svg
          viewBox="0 0 345 167"
          className="w-full h-full"
          preserveAspectRatio="none"
          role="img"
          aria-label={image!.alt}
        >
          <defs>
            <clipPath
              id={`mask-shape-m-${shape}`}
              clipPathUnits="objectBoundingBox"
            >
              <MaskShapeClipPath variant={shape} />
            </clipPath>
          </defs>
          <image
            href={image!.src}
            width="345"
            height="167"
            preserveAspectRatio="xMidYMid slice"
            clipPath={`url(#mask-shape-m-${shape})`}
          />
        </svg>
      </div>
      {/* Desktop */}
      <div className="hidden md:block relative w-full aspect-[345/110] overflow-hidden rounded-2xl">
        <svg
          viewBox="0 0 345 110"
          className="w-full h-full"
          preserveAspectRatio="none"
          role="img"
          aria-label={image!.alt}
        >
          <defs>
            <clipPath
              id={`mask-shape-d-${shape}`}
              clipPathUnits="objectBoundingBox"
            >
              <MaskShapeClipPath variant={shape} />
            </clipPath>
          </defs>
          <image
            href={image!.src}
            width="345"
            height="110"
            preserveAspectRatio="xMidYMid slice"
            clipPath={`url(#mask-shape-d-${shape})`}
          />
        </svg>
      </div>
    </>
  );
};

// Generate normalized clip path based on MaskShape variant
const MaskShapeClipPath = ({ variant }: { variant: MaskShapeVariant }) => {
  // These paths are normalized to 0-1 range for use as clip paths
  switch (variant) {
    case "four_vertical_even":
      return (
        <>
          <path d="M0.363 0.5C0.363 0.776 0.282 1 0.182 1C0.081 1 0 0.776 0 0.5C0 0.224 0.081 0 0.182 0C0.282 0 0.363 0.224 0.363 0.5Z" />
          <path d="M0.576 0.5C0.576 0.776 0.494 1 0.394 1C0.294 1 0.212 0.776 0.212 0.5C0.212 0.224 0.294 0 0.394 0C0.494 0 0.576 0.224 0.576 0.5Z" />
          <path d="M0.788 0.5C0.788 0.776 0.706 1 0.606 1C0.506 1 0.424 0.776 0.424 0.5C0.424 0.224 0.506 0 0.606 0C0.706 0 0.788 0.224 0.788 0.5Z" />
          <path d="M1 0.5C1 0.776 0.919 1 0.818 1C0.718 1 0.637 0.776 0.637 0.5C0.637 0.224 0.718 0 0.818 0C0.919 0 1 0.224 1 0.5Z" />
        </>
      );
    case "two_vertical_uneven":
      return (
        <>
          <path d="M0.4 0.5C0.4 0.776 0.31 1 0.2 1C0.09 1 0 0.776 0 0.5C0 0.224 0.09 0 0.2 0C0.31 0 0.4 0.224 0.4 0.5Z" />
          <path d="M1 0.5C1 0.776 0.838 1 0.638 1C0.437 1 0.275 0.776 0.275 0.5C0.275 0.224 0.437 0 0.638 0C0.838 0 1 0.224 1 0.5Z" />
        </>
      );
    case "wave":
      return (
        <>
          <path d="M0.419 0.5C0.419 0.224 0.502 0 0.605 0C0.707 0 0.791 0.224 0.791 0.5H0.419Z" />
          <path d="M0 0.5C0 0.224 0.083 0 0.186 0C0.289 0 0.372 0.224 0.372 0.5H0Z" />
          <path d="M1 0.5C1 0.776 0.917 1 0.814 1C0.711 1 0.628 0.776 0.628 0.5H1Z" />
          <path d="M0.581 0.5C0.581 0.776 0.498 1 0.395 1C0.293 1 0.209 0.776 0.209 0.5H0.581Z" />
        </>
      );
    case "split_half_circle":
      return (
        <>
          <path d="M0 1C0.277 1 0.501 0.776 0.501 0.5C0.501 0.224 0.277 0 0 0V1Z" />
          <path d="M0.501 1C0.777 1 1 0.776 1 0.5C1 0.224 0.777 0 0.501 0V1Z" />
        </>
      );
    case "three_horizontal_even":
      return (
        <>
          <path d="M1 0.225C1 0.349 0.776 0.45 0.5 0.45C0.224 0.45 0 0.349 0 0.225C0 0.101 0.224 0 0.5 0C0.776 0 1 0.101 1 0.225Z" />
          <path d="M1 0.5C1 0.624 0.776 0.725 0.5 0.725C0.224 0.725 0 0.624 0 0.5C0 0.376 0.224 0.275 0.5 0.275C0.776 0.275 1 0.376 1 0.5Z" />
          <path d="M1 0.775C1 0.899 0.776 1 0.5 1C0.224 1 0 0.899 0 0.775C0 0.651 0.224 0.55 0.5 0.55C0.776 0.55 1 0.651 1 0.775Z" />
        </>
      );
    case "three_vertical_uneven":
      return (
        <>
          <path d="M0.4 0.5C0.4 0.776 0.31 1 0.2 1C0.09 1 0 0.776 0 0.5C0 0.224 0.09 0 0.2 0C0.31 0 0.4 0.224 0.4 0.5Z" />
          <path d="M0.6 0.5C0.6 0.776 0.51 1 0.4 1C0.29 1 0.2 0.776 0.2 0.5C0.2 0.224 0.29 0 0.4 0C0.51 0 0.6 0.224 0.6 0.5Z" />
          <path d="M1 0.5C1 0.776 0.869 1 0.708 1C0.546 1 0.415 0.776 0.415 0.5C0.415 0.224 0.546 0 0.708 0C0.869 0 1 0.224 1 0.5Z" />
        </>
      );
    case "three_vertical_uneven_mirrored":
      return (
        <>
          <path d="M1 0.5C1 0.77625 0.9105 1 0.8 1C0.6895 1 0.6 0.77625 0.6 0.5C0.6 0.22375 0.6895 0 0.8 0C0.9105 0 1 0.22375 1 0.5Z" />
          <path d="M0.785 0.5C0.785 0.77625 0.6955 1 0.585 1C0.4745 1 0.385 0.77625 0.385 0.5C0.385 0.22375 0.4745 0 0.585 0C0.6955 0 0.785 0.22375 0.785 0.5Z" />
          <path d="M0.585 0.5C0.585 0.77625 0.454 1 0.2925 1C0.131 1 0 0.77625 0 0.5C0 0.22375 0.131 0 0.2925 0C0.454 0 0.585 0.22375 0.585 0.5Z" />
        </>
      );
    case "circle":
      return <circle cx="0.5" cy="0.5" r="0.5" />;
    default:
      return <circle cx="0.5" cy="0.5" r="0.5" />;
  }
};

const EventHero = ({
  eventType,
  date,
  endDate,
  subline,
  title,
  description,
  image,
  playbackId,
  maskShape = "four_vertical_even",
  infoItems = [],
  ctaLabel = "Jetzt anmelden",
  ctaLink,
  hideCta,
  onCtaClick,
  memberOnly,
  isMember,
  isSignedUp,
  dateChanged: heroDateChanged,
  isCancelled: heroIsCancelled,
  onCancelSignup,
  className,
  ...rest
}: EventHeroProps) => {
  const now = useNow();
  const relevantDate = endDate ?? date;
  const isPreEvent = now ? relevantDate >= new Date(now.getFullYear(), now.getMonth(), now.getDate()) : true;
  const isAfterEvent = !isPreEvent;

  // DiaTalk archive videos are always member-only, even if the event itself is public
  const isDiaTalkVideo = eventType === "diaTalk" && !!playbackId;
  const effectiveMemberOnly = memberOnly || isDiaTalkVideo;
  const pathname = usePathname();
  const { userId } = useAuth();

  const showVideo = isAfterEvent && !!playbackId && !(effectiveMemberOnly && !isMember);
  const showLockedVideo = isAfterEvent && !!playbackId && effectiveMemberOnly && !isMember;

  // Show button only before event and if not hidden
  const showButton = isPreEvent && (ctaLink || onCtaClick) && !hideCta;


  // Build list items: date is always first, then custom info items
  const listItems = useMemo(() => {
    const items: Array<{ icon: ListItemIconName; text: string }> = [];

    // 1. Date (always shown)
    let dateText: string;
    if (endDate) {
      // Multi-day event: "02. Juli bis 11. Juli 2026"
      const sameYear = date.getFullYear() === endDate.getFullYear();
      const sameMonth = sameYear && date.getMonth() === endDate.getMonth();
      if (sameMonth) {
        dateText = `${format(date, "dd.", { locale: de })} bis ${format(
          endDate,
          "dd. MMMM yyyy",
          { locale: de },
        )}`;
      } else if (sameYear) {
        dateText = `${format(date, "dd. MMMM", { locale: de })} bis ${format(
          endDate,
          "dd. MMMM yyyy",
          { locale: de },
        )}`;
      } else {
        dateText = `${format(date, "dd. MMMM yyyy", {
          locale: de,
        })} bis ${format(endDate, "dd. MMMM yyyy", { locale: de })}`;
      }
    } else {
      // Single-day event: include time if not midnight
      const hasTime = date.getHours() !== 0 || date.getMinutes() !== 0;
      dateText = hasTime
        ? `${format(date, "EEEE, dd. MMM yyyy - HH:mm", { locale: de })} Uhr`
        : format(date, "EEEE, dd. MMM yyyy", { locale: de });
    }
    items.push({
      icon: "date",
      text: dateText,
    });

    // 2. Add custom info items from Storyblok
    infoItems.forEach((item) => {
      if (item.text) {
        items.push({ icon: item.icon, text: item.text });
      }
    });

    return items;
  }, [date, endDate, infoItems]);

  return (
    <section
      className={cn("bg-sand-10 pt-6 pb-12 md:pt-8 md:pb-16 ", className)}
      {...rest}
    >
      <GridContainer>
        <div className="col-span-12 md:col-span-8 md:col-start-3 lg:col-span-8 lg:col-start-3">
          {/* Header: Tag + Button */}
          <div
            className={cn(
              "flex items-center",
              eventType ? "justify-between" : "justify-end",
            )}
          >
            <div className="flex items-center gap-1">
              {eventType && <EventTag variant={eventType} memberOnly={effectiveMemberOnly} />}
              {effectiveMemberOnly && <MemberLockIcon locked={!isMember} />}
            </div>
            {isSignedUp ? (
              <button
                type="button"
                onClick={() => document.getElementById("event-signup-section")?.scrollIntoView({ behavior: "smooth" })}
                className={cn(
                  "inline-flex items-center gap-1 rounded-full px-2 py-1 text-subline-sm cursor-pointer transition-colors",
                  heroIsCancelled ? "bg-red-100 text-white" : heroDateChanged ? "bg-sun-50 text-black hover:bg-sun-75" : "bg-sky-50 text-black hover:bg-sky-75"
                )}
              >
                {heroIsCancelled || heroDateChanged ? (
                  <WarningTriangle className={cn("size-4 shrink-0", heroIsCancelled ? "text-white" : "text-black")} />
                ) : (
                  <CheckCircleFilled className="size-4 text-black shrink-0" />
                )}
                {heroIsCancelled ? "Termin abgesagt" : heroDateChanged ? "Termin geändert" : isAfterEvent ? "Du hast teilgenommen" : "Du nimmst teil"}
              </button>
            ) : (
              <>
                {showButton && onCtaClick && (
                  <Button variant="sun" onClick={onCtaClick}>
                    {ctaLabel}
                  </Button>
                )}
                {effectiveMemberOnly && !isMember && !onCtaClick && (
                  <Button variant="sun" asChild>
                    {/* Gleiche Behandlung wie ein gesperrter Artikel oder ein
                        gesperrtes Interview: Der Nutzer wollte an einen
                        mitgliederexklusiven Inhalt und soll nach dem Kauf
                        hierher zurueck. Ohne den Intent landete er auf dem
                        Dashboard und muesste das Event neu suchen.

                        Ziel ist /onboarding/membership statt /onboarding/trial
                        — dort steht die Planauswahl direkt, das spart einen
                        Klick. */}
                    <Link
                      href={userId ? "/onboarding/membership" : `/login?redirect=${encodeURIComponent(pathname)}`}
                      onClick={() => { if (!isMember) setReturnIntent(pathname, "event_access"); }}
                      data-cta-location="event_hero"
                      data-cta-label="Jetzt Member werden"
                    >
                      Jetzt Member werden
                    </Link>
                  </Button>
                )}
                {showButton && !onCtaClick && ctaLink && (
                  <Button variant="sun" asChild>
                    <Link href={ctaLink}>{ctaLabel}</Link>
                  </Button>
                )}
              </>
            )}
          </div>

          {/* Title section */}
          <div className="mt-6 flex flex-col  gap-1 md:gap-2">
            {subline && (
              <p className="text-subline-bold text-red-95">{subline}</p>
            )}
            <h1 className="text-title-event text-black-95">{title}</h1>
          </div>

          {/* Masked image/video */}
          {showLockedVideo ? (
            <div className="relative mt-6 md:mt-8 aspect-video rounded-3xl overflow-hidden">
              <Image
                src={`https://image.mux.com/${playbackId}/thumbnail.webp?time=5`}
                alt={title}
                fill
                sizes="(max-width: 768px) 100vw, 66vw"
                className="object-cover blur-lg scale-110"
              />
              <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center gap-4 px-6 text-center">
                <MemberLockIcon locked />
                <p className="text-copy text-white">
                  Dieses Video ist exklusiv für Mitglieder.
                </p>
                <Button variant="sun" asChild>
                  {/* Wie oben: gesperrter Inhalt, also Intent setzen und in
                      die Planauswahl statt auf die Marketingseite. */}
                  <Link
                    href={userId ? "/onboarding/membership" : `/login?redirect=${encodeURIComponent(pathname)}`}
                    onClick={() => setReturnIntent(pathname, "event_access")}
                    data-cta-location="event_hero"
                    data-cta-label="Jetzt Member werden"
                  >
                    Jetzt Member werden
                  </Link>
                </Button>
              </div>
            </div>
          ) : (
            <div className="relative mt-6 md:mt-8">
              <MaskedMedia
                image={image}
                playbackId={playbackId}
                shape={maskShape}
                showVideo={showVideo}
                eventType={eventType}
              />
            </div>
          )}

          {/* Description */}
          {description && (
            <div className="text-copy md:max-w-3xl text-black-95 mt-6 md:mt-8 [&>p]:mb-0">
              {description}
            </div>
          )}

          {/* Auto-generated list */}
          <ListBlock
            variant="divider"
            className="mt-6 md:mt-8 md:max-w-xl text-black-95"
          >
            {listItems.map((item, index) => (
              <ListItem
                icon={item.icon}
                key={item.icon}
                className={
                  index === 0 && endDate ? "[&>span]:text-copy-bold" : undefined
                }
              >
                {item.text}
              </ListItem>
            ))}
          </ListBlock>
        </div>
      </GridContainer>
    </section>
  );
};

export { EventHero };
export type { EventHeroProps };
